import React from 'react';
import { Contact } from './types';
import { FormErrors } from '../../../types';
import { CategoryDropdown } from '../category';

interface ContactInfoSectionProps {
  contactData: Omit<Contact, 'id' | 'dateCreated' | 'dateUpdated' | 'whoUpdated'>;
  errors: FormErrors;
  onChange: (field: keyof Contact, value: string | number | boolean) => void;
  enableAccess?: boolean;
  onEnableAccessChange?: (value: boolean) => void;
  categoryName?: string | null;
  canEnableAccess?: boolean;
  isTypeDisabled?: boolean;
}

const ContactInfoSection: React.FC<ContactInfoSectionProps> = ({
  contactData,
  errors,
  onChange,
  enableAccess = false,
  onEnableAccessChange,
  categoryName,
  canEnableAccess = false,
  isTypeDisabled = false
}) => {
  const handleInputChange = (field: keyof Contact, value: string | number | boolean) => {
    onChange(field, value);
  };

  const shouldShowCheckbox = categoryName === 'Tenant' || categoryName === 'Client';

  return (
    <div className="form-section-content">
      <div className="app-form-row">
        <div className="app-form-group">
          <label htmlFor="type" className="app-form-label required">
            Contact Type
          </label>
          <CategoryDropdown
            parentType="contact"
            value={contactData.typeId}
            onChange={(value) => handleInputChange('typeId', value)}
            className={errors.type ? 'error' : ''}
            required={true}
            disabled={isTypeDisabled}
          />
          {errors.type && <div className="app-form-error">{errors.type}</div>}
        </div>
      </div>

      {/* Enable Access Checkbox */}
      {shouldShowCheckbox && (
        <div className="app-form-row">
          <div className="app-form-group">
            <label className="app-form-label">
              <input
                type="checkbox"
                checked={enableAccess}
                onChange={(e) => onEnableAccessChange?.(e.target.checked)}
                disabled={!canEnableAccess}
                style={{ marginRight: '0.5rem' }}
              />
              Enable {categoryName} access
            </label>
            {!canEnableAccess && (
              <div className="app-form-hint" style={{ fontSize: '0.875rem', color: '#64748b', marginTop: '0.25rem' }}>
                Primary email address is required to enable access
              </div>
            )}
          </div>
        </div>
      )}

      <div className="app-form-row">
        <div className="app-form-group">
          <label htmlFor="firstName" className="app-form-label">
            First Name
          </label>
          <input
            id="firstName"
            type="text"
            className={`app-form-input ${errors.firstName ? 'error' : ''}`}
            value={contactData.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            placeholder="Enter first name"
          />
          {errors.firstName && <div className="app-form-error">{errors.firstName}</div>}
        </div>

        <div className="app-form-group">
          <label htmlFor="lastName" className="app-form-label">
            Last Name
          </label>
          <input
            id="lastName"
            type="text"
            className={`app-form-input ${errors.lastName ? 'error' : ''}`}
            value={contactData.lastName}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            placeholder="Enter last name"
          />
          {errors.lastName && <div className="app-form-error">{errors.lastName}</div>}
        </div>
      </div>

      <div className="app-form-group">
        <label htmlFor="businessName" className="app-form-label">
          Business Name <span className="app-form-hint">(at least one of first, last, or business name required)</span>
        </label>
        <input
          id="businessName"
          type="text"
          className="app-form-input"
          value={contactData.businessName}
          onChange={(e) => handleInputChange('businessName', e.target.value)}
          placeholder="Enter business name (optional)"
        />
      </div>

      <div className="app-form-group">
        <label htmlFor="defaultCategoryId" className="app-form-label">
          Default expense category
          <span className="app-form-hint"> (optional — prefills expense category when this contact is the vendor)</span>
        </label>
        <CategoryDropdown
          parentType="expense"
          value={contactData.defaultCategoryId ?? 0}
          onChange={(value) => handleInputChange('defaultCategoryId', value)}
          placeholder="None"
        />
      </div>

      <div className="app-form-group">
        <label htmlFor="notes" className="app-form-label">
          Notes
        </label>
        <textarea
          id="notes"
          className="app-form-textarea"
          value={contactData.notes}
          onChange={(e) => handleInputChange('notes', e.target.value)}
          placeholder="Enter any additional notes (optional)"
          rows={4}
        />
      </div>

      <div className="app-form-group">
        <label className="app-form-label">
          <input
            type="checkbox"
            checked={!!contactData.includeInExport}
            onChange={(e) => handleInputChange('includeInExport', e.target.checked)}
            style={{ marginRight: '0.5rem' }}
          />
          Include in export
        </label>
      </div>

      <div className="app-form-group">
        <label className="app-form-label">
          <input
            type="checkbox"
            checked={!!contactData.isPreferred}
            onChange={(e) => handleInputChange('isPreferred', e.target.checked)}
            style={{ marginRight: '0.5rem' }}
          />
          Preferred
        </label>
      </div>
    </div>
  );
};

export default ContactInfoSection;
